import { NavigationButtons } from "@/components/NavigationButtons/NavigationButtons";
import { useFilesConfirmation } from "@/hooks/filesConfirmation";
import { useCurrentUploadId, useCurrentUploadQuery } from "@/hooks/uploads";
import FormField from "@ingka/form-field";
import Hyperlink from "@ingka/hyperlink";
import InputField from "@ingka/input-field";
import Loading from "@ingka/loading/Loading";
import Text from "@ingka/text";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { AuthenticatedImage } from "../AuthenticatedResource/AuthenticatedImage";
import { ConfirmFilesOriginality } from "./ConfirmFilesOriginality";
import { AssetWithOriginalName } from "@/hooks/types";
import { useAuth } from "@/hooks/auth";
import { SIMILARITY_SEARCH_URL, INSTRUCTION_VIDEOS_URL } from "@/constants";
import { ValidationError } from "./ValidationError";
import { toRouteError } from "@/errors/routeError";

export const ImageFilesConfirmation = () => {
  const navigate = useNavigate();
  const { isIngkaUser } = useAuth().user;
  const uploadId = useCurrentUploadId();
  const currentUploadQuery = useCurrentUploadQuery();
  const filesConfirmationMutation = useFilesConfirmation();
  const [isValid, setIsValid] = useState(true);
  const [originalNames, setOriginalNames] = useState<AssetWithOriginalName[]>(
    [],
  );
  const [selectedRadioBtnOption, setSelectedRadioBtnOption] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    if (currentUploadQuery.data) {
      if (currentUploadQuery.data.allOriginalConfirmation !== null) {
        setSelectedRadioBtnOption(
          currentUploadQuery.data.allOriginalConfirmation,
        );
      }
      setOriginalNames(
        currentUploadQuery.data.assets.map(
          ({ id, name, originalName, filename }) => ({
            id,
            name,
            originalName,
            filename,
          }),
        ),
      );
    }
  }, [currentUploadQuery.data]);

  if (currentUploadQuery.isLoading) {
    <Loading text="Loading current upload assets" />;
  }

  if (currentUploadQuery.isError) {
    throw toRouteError(currentUploadQuery.error, {
      scope: "upload",
      uploadId,
    });
  }

  const allOriginalNameIsEmpty = originalNames.every(
    (asset) => !asset.originalName,
  );

  const onOriginalNameChange = (update: AssetWithOriginalName) => {
    setIsValid(true);
    setOriginalNames((prevState) =>
      prevState.map((asset) => (asset.id === update.id ? update : asset)),
    );
  };

  const saveFilesConfirmation = async (assets: AssetWithOriginalName[]) => {
    if (
      selectedRadioBtnOption === null ||
      (selectedRadioBtnOption === false && allOriginalNameIsEmpty)
    ) {
      setIsValid(false);
      return;
    }
    await filesConfirmationMutation.uploadMutateAsync({
      id: Number(uploadId),
      updateUploadDto: {
        allOriginalConfirmation: assets.every((asset) => !asset.originalName),
      },
    });

    await Promise.all(
      assets.map(({ id, originalName }) =>
        filesConfirmationMutation.assetMutateAsync({
          id,
          updateAssetDto: {
            originalName: originalName?.trim(),
          },
        }),
      ),
    );

    if (!filesConfirmationMutation.isPending) {
      navigate(`/uploads/${uploadId}/mandatory-metadata`);
    }
  };

  return (
    <>
      <ConfirmFilesOriginality
        radioBtnState={selectedRadioBtnOption}
        onChange={(value) => {
          setSelectedRadioBtnOption(value);
          setIsValid(true);
        }}
        disabled={!allOriginalNameIsEmpty}
      />

      {selectedRadioBtnOption === false && (
        <section className="mt-space300 flex-col space-y-space100">
          <Text tagName="h3" headingSize="xs" className="mb-space100">
            Please provide the original names of the images that have been
            edited
          </Text>
          {isIngkaUser && (
            <Text tagName="p">
              If you are unsure if an image already exists in Ingka DAM or what
              the original name is, you can use the similarity search in{" "}
              <Hyperlink url={SIMILARITY_SEARCH_URL} text="Ladan" newWindow />{" "}
              to find it. Click{" "}
              <Hyperlink url={INSTRUCTION_VIDEOS_URL} text="here" newWindow />{" "}
              for instructions on how to use the similarity search.
            </Text>
          )}
          <ul className="grid grid-cols-[20.4rem_20.4rem_20.4rem] gap-x-space125 gap-y-space100">
            {originalNames.map(({ id, name, originalName, filename }) => (
              <li key={id}>
                <Text tagName="span" bodySize="s">
                  {filename || name}
                </Text>
                <AuthenticatedImage
                  assetId={id}
                  className="h-[15.3rem] w-full object-cover"
                />
                <FormField valid="error" className="mb-0 mt-space50">
                  <InputField
                    iconPosition="leading"
                    id={String(id)}
                    label="Name of original asset"
                    type="text"
                    value={originalName || ""}
                    disabled={filesConfirmationMutation.isPending}
                    onChange={(e) =>
                      onOriginalNameChange({
                        id,
                        name,
                        filename,
                        originalName: e.target.value.trim(),
                      })
                    }
                  />
                </FormField>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section className="my-space250 ml-auto flex flex-row items-center">
        {!isValid && selectedRadioBtnOption === null && (
          <ValidationError message="Choice is required" />
        )}
        {!isValid && selectedRadioBtnOption === false && (
          <ValidationError message="Original image name is required" />
        )}

        <NavigationButtons
          onPrev={() => {}}
          isNextLoading={filesConfirmationMutation.isPending}
          isPrevLoading={filesConfirmationMutation.isPending}
          disabledNext={!isValid}
          disabledPrev={true}
          nextButtonTitle={
            filesConfirmationMutation.isPending ? "Processing..." : "Continue"
          }
          onContinue={() => saveFilesConfirmation(originalNames)}
        />
      </section>
    </>
  );
};
